import React, { useState } from "react";
import styles from "./PxProductTile.module.scss";
import classnames from "classnames";
import { ProductTile } from "@swordsweeper/ssi-ui-component-library";
import { Link } from "react-router-dom";
import { DistributorProduct, Product } from "@src/types/product";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import DistributorLogo from "@src/components/common/DistributorLogo";
import * as CartApi from "@src/api/CartApi";
import { useCartStore } from "@src/state/cartState";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import ProductImage from "../ProductImage";
import {
  getAddToCartButtonDetails,
  getProductVariantSubheading,
} from "@src/utils/product";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { DistributorConnectionStatuses } from "../AccountDistributorSettings/DistributorConnectionConstants";
import { DistributorApiHealthStatuses } from "@src/constants/DistributorApiHealthStatuses";
import FavoriteHeartButton from "@src/components/common/FavoriteHeartButton/FavoriteHeartButton";

interface PxProductTileProps {
  className?: string;
  product: Product | DistributorProduct;
  imageSizePx?: number;
  tileWidth?: string; // Optional prop to specify tile width, if needed for layout purposes
  tileHeight?: string; // Optional prop to specify tile height, if needed for layout purposes
  onSetIsAddingToCart?: (isAdding: boolean) => void; // Optional callback to inform parent component when add to cart action is in progress
  disableAddToCartButton?: boolean; // Optional prop to disable the add to cart button externally
  idContext?: string; // Optional prop to provide context for unique ID generation (e.g., for testing purposes)
  hideFavoriteHeart?: boolean; // Hide the favorite heart overlay (e.g. on the Favorites page where it's redundant)
}

export default function PxProductTile({
  className,
  product,
  imageSizePx = 220,
  tileWidth,
  tileHeight,
  onSetIsAddingToCart,
  disableAddToCartButton = false,
  idContext = "product-tile",
  hideFavoriteHeart = false,
}: PxProductTileProps) {
  const { showToast } = useToast();
  const { refreshCart } = useCartStore();
  const [isAdding, setIsAdding] = useState(false);
  const { distributorApiHealthStatuses, getDistributorConnection } =
    useCurrentUserStore();

  let productData: Product | null = null;
  let distributorProductData: any = null;
  let addToCartButtonText: string | null = null;
  let addToCartButtonDisabled: boolean | null = null;

  if ("product" in product) {
    const { product: pd, ...dpd } = product;
    distributorProductData = dpd;
    productData = pd;

    const distributorApiHealthStatus = distributorApiHealthStatuses?.find(
      (status) =>
        status.distributor === distributorProductData.distributor.slug,
    )?.status;
    const isDistributorApiHealthStatusHealthy =
      distributorApiHealthStatus === DistributorApiHealthStatuses.Healthy;

    // Check actual connection status from distributorConnections (not just livePricing)
    const connection = getDistributorConnection(
      distributorProductData.distributor.slug,
    );
    const isConnectedToDistributor =
      connection?.status === DistributorConnectionStatuses.Connected;

    const hasPerfectApiConnection =
      isConnectedToDistributor && isDistributorApiHealthStatusHealthy;

    const { cartButtonText, cartButtonDisabled } = getAddToCartButtonDetails(
      distributorProductData.livePricing,
      hasPerfectApiConnection,
    );
    addToCartButtonText = cartButtonText;
    addToCartButtonDisabled = cartButtonDisabled;
  } else {
    productData = product;
  }

  const handleAddToCart = async (
    event?: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!distributorProductData.id || isAdding) return;
    setIsAdding(true);
    onSetIsAddingToCart?.(true);
    try {
      await CartApi.addToCart(String(distributorProductData.id), 1);
      await refreshCart();
      showToast({
        message: "Added to cart",
        variant: "success",
      });
    } catch (error) {
      console.error("Failed to add to cart", error);
      showToast({
        message: "Error adding item to cart",
        variant: "error",
      });
    } finally {
      setIsAdding(false);
      onSetIsAddingToCart?.(false);
    }
  };

  const wrapJsxInLink = (jsx: React.JSX.Element, location: string) => (
    <Link
      key={`${location}-product-link-${productData!.id}`}
      data-testid={`${location}-product-link-${productData!.id}`}
      className="link text-default width-fit-content"
      to={`/product/${productData!.id}/`}
    >
      {jsx}
    </Link>
  );

  const computedStyles: React.CSSProperties = {};
  if (!!tileWidth) {
    computedStyles.width = tileWidth;
  }
  if (!!tileHeight) {
    computedStyles.height = tileHeight;
  }

  const productId = String(productData.id ?? "");
  const isScheduledProduct = !!distributorProductData?.schedule;

  const hasPrice = !!productData.price && productData.price > 0;
  return (
    <div className={styles.tileWrapper}>
      {!!productId && !hideFavoriteHeart && (
        <FavoriteHeartButton
          productId={productId}
          variant="tile"
          className={styles.tileHeartOverlay}
        />
      )}
      <ProductTile
        key={`product-tile-${productData.id}`}
        className={classnames(
          "background-white flex-1",
          className,
          styles.root,
        )}
        image={wrapJsxInLink(
          <ProductImage imageSrc={productData.imageUrl} sizePx={imageSizePx} />,
          "image",
        )}
        imageAlt="Product Image"
        imageHeight={imageSizePx}
        imageWidth={imageSizePx}
        productDescription={
          !distributorProductData ? (
            <span
              className={styles.variantSubheading}
              style={{ maxWidth: `${imageSizePx}px` }}
              data-testid={`product-tile-variant-subheading-${productData.id}`}
            >
              {getProductVariantSubheading(productData)}
            </span>
          ) : null
        }
        productPrice={
          !!distributorProductData ? (
            <div
              className={styles.priceArea}
              data-testid={`product-tile-distributor-price-area-${distributorProductData.id}`}
            >
              <div className="margin-bottom-xs">
                <div className={styles.priceRow}>
                  <PriceDisplay
                    amount={hasPrice ? productData.price : null}
                    className={
                      hasPrice ? styles.priceValue : styles.priceUnavailable
                    }
                    unavailableText="Price Unavailable"
                    data-testid={`product-tile-distributor-price-display-${distributorProductData.id}`}
                  />
                  {!!distributorProductData.distributor?.slug && (
                    <DistributorLogo
                      width={70}
                      distributorSlug={distributorProductData.distributor.slug}
                    />
                  )}
                </div>
                {distributorProductData.totalOrdered > 0 && (
                  <div
                    className="text-xs text-light text-italic"
                    data-testid={`product-tile-distributor-previously-ordered-${distributorProductData.id}`}
                  >
                    {distributorProductData.totalOrdered} previously ordered
                  </div>
                )}
              </div>
              {addToCartButtonDisabled && isScheduledProduct && !isAdding ? (
                <span
                  className="text-secondary text-italic text-xs"
                  data-testid={`product-tile-scheduled-contact-distributor-${distributorProductData.id}`}
                >
                  {addToCartButtonText}
                </span>
              ) : (
                <button
                  id={`${idContext}-add-to-cart-button-${distributorProductData.id}`}
                  data-testid={`add-to-cart-button-${distributorProductData.id}`}
                  type="button"
                  className={classnames(
                    "btn btn-primary btn-bordered btn-sm",
                    styles.addToCartButton,
                  )}
                  onClick={handleAddToCart}
                  disabled={
                    !distributorProductData.id ||
                    addToCartButtonDisabled ||
                    isAdding ||
                    disableAddToCartButton
                  }
                >
                  {isAdding ? "Adding..." : addToCartButtonText}
                </button>
              )}
            </div>
          ) : (
            <PriceDisplay
              amount={hasPrice ? productData.price : null}
              className={!hasPrice ? "text-light text-sm" : undefined}
              unavailableText="Price Unavailable"
              data-testid={`product-tile-price-display-${productData.id}`}
            />
          )
        }
        productPriceSubtext={hasPrice && !distributorProductData ? "& up" : ""}
        productTitle={wrapJsxInLink(
          <span>{productData.itemName}</span>,
          "item-name",
        )}
        renderBadge={() => null}
        showDescription
        showPrice
        showTitle
        style={computedStyles}
      />
    </div>
  );
}
